import React, { ReactElement } from 'react';
import ReactSelect from 'react-select';

import { toJsIfImmutable } from 'shared/functions';

import type { ActionMeta, InputActionMeta, OnChangeValue } from 'react-select';

import { findOption, getReactSelectStyles, tryToStringify, valueToOption } from './functions';

/** @deprecated TODO remove in DEV-25601 */
export interface SelectProps<V, O, IsMulti extends boolean = false> {
  id?: string;
  valueKey: string;
  labelKey: string;
  clearable?: boolean;
  multi?: IsMulti;
  disabled?: boolean;
  isLoading?: boolean;
  closeOnSelect?: boolean;
  options?: O[];
  value?: V;
  styles?: Record<string, string>;
  placeholder?: ReactElement | string;
  noResultsText?: ReactElement | string;
  optionRenderer?: (option: V) => ReactElement | string;
  valueRenderer?: (option: V) => ReactElement | string;
  formatOptionLabel?: (option: V) => ReactElement | string;
  onChange?: (newValue: OnChangeValue<V, IsMulti>, actionMeta: ActionMeta<V>) => void;
  onInputChange?: (newValue: string, actionMeta: InputActionMeta) => void;
  filterOptions?: (options: V[]) => V[];
  filterOption?: (option: V, inputValue: string) => boolean;
  filterValue?: (value: V) => boolean;
  isFixedOption?: (option: V) => boolean;
}

/** @deprecated TODO remove in DEV-25601 */
const emptyArray = [];

/** @deprecated TODO remove in DEV-25601 */
// eslint-disable-next-line import/no-default-export
export default class Select<V, O, IsMulti extends boolean> extends React.Component<SelectProps<V, O, IsMulti>> {
  private value: V;

  static defaultProps: SelectProps<unknown, unknown> = {
    valueKey: 'value',
    labelKey: 'label',
  };

  getOptionValue = (option: V) => {
    const { valueKey, valueRenderer } = this.props;

    return valueRenderer ? valueRenderer(option) : option[valueKey];
  };

  getOptionLabel = (initialOption: V) => {
    const { valueKey, labelKey, options, optionRenderer } = this.props;

    const option = valueToOption(initialOption, options, valueKey);

    return optionRenderer ? optionRenderer(option) : option[labelKey];
  };

  render() {
    const {
      styles,
      options,
      clearable,
      multi,
      disabled,
      valueKey,
      onChange,
      formatOptionLabel,
      isFixedOption,
      filterValue = () => true,
    } = this.props;

    const jsValue = toJsIfImmutable(this.props.value);
    let value: any;
    if (multi) {
      if (Array.isArray(jsValue)) {
        value = jsValue.map(findOption(valueKey, options)).filter(filterValue);
      } else {
        value = jsValue;
      }
    } else {
      value = findOption(valueKey, options)(jsValue);
    }

    if (tryToStringify(this.value) !== tryToStringify(value)) {
      this.value = value;
    }

    // https://github.com/JedWatson/react-select/issues/3066
    if (this.value === undefined) {
      this.value = null;
    }

    const selectStyles = getReactSelectStyles(multi, isFixedOption, styles);

    return (
      <ReactSelect
        isClearable={clearable === undefined ? true : clearable}
        isMulti={multi}
        isDisabled={disabled}
        menuPlacement="auto"
        className="Select"
        classNamePrefix="Select-control"
        getOptionValue={this.getOptionValue}
        getOptionLabel={this.getOptionLabel}
        formatOptionLabel={formatOptionLabel}
        {...this.props}
        styles={selectStyles}
        value={this.value}
        onChange={(updatedValue: any, triggeredAction: any) => {
          if (!onChange) {
            return;
          }

          const { action, removedValue } = triggeredAction;

          let returningValue = updatedValue;
          switch (action) {
            case 'remove-value':
            case 'pop-value':
              if (isFixedOption && isFixedOption(removedValue)) {
                return;
              }
              break;
            case 'clear':
              if (Array.isArray(updatedValue && isFixedOption)) {
                returningValue = updatedValue.filter((item: any) => isFixedOption(item));
              }
              break;

            default:
              break;
          }

          if (multi && updatedValue === null) {
            returningValue = emptyArray;
          }

          onChange(returningValue, triggeredAction);
        }}
      />
    );
  }
}

/** @deprecated TODO remove in DEV-25601 */
export * from './functions';
